// Recipe-book digest for the weekly planner. Compresses the user's
// cookable recipes (bundled library + their user_recipes) into the
// compact rows plan-week's prompt reads: slug, title, coarse macros,
// and the "dusty" forgotten-recipe flag the user explicitly wants
// resurfaced.
//
// Nutrition rides only when recipeNutrition can resolve at least half
// the ingredients — a 2-of-9-resolved kcal number is a lie, and the
// planner would goal-score on it.

import { RECIPES, totalTimeMin } from "../data/recipes";
import { recipeNutrition } from "./nutrition";

// "Forgotten": cooked, but not in the last 45 days — or saved 30+
// days ago and never cooked at all.
const DUSTY_COOKED_DAYS = 45;
const DUSTY_NEVER_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

export const DIGEST_CAP = 80;

/**
 * @param {object} opts
 * @param {Array}  opts.userRecipes   — useUserRecipes().recipes rows
 * @param {Map}    opts.lastCookedMap — fetchLastCookedMap(userId)
 * @param {Array}  opts.pantry        — usePantry rows (for recipeNutrition)
 * @param {object} opts.brandNutrition — useBrandNutrition() (optional)
 * @param {function} opts.getInfo     — useIngredientInfo().getInfo
 * @param {string} opts.goal          — factors.goal (orders the cap cut)
 */
export function buildBookDigest({
  userRecipes = [],
  lastCookedMap = new Map(),
  pantry = [],
  brandNutrition,
  getInfo,
  goal = "balanced",
} = {}) {
  const now = Date.now();
  const rows = [];
  const seen = new Set();

  const push = (recipe, ownerUserId) => {
    if (!recipe?.slug || !recipe?.title || seen.has(recipe.slug)) return;
    // Compound-ingredient scratch recipes (pesto, stock, sourdough…)
    // produce a pantry item, not a meal — they don't fill a dinner slot.
    if (recipe.produces) return;
    seen.add(recipe.slug);

    const lastMs = lastCookedMap.get(recipe.slug) ?? null;
    const lastCookedDays = lastMs != null ? Math.floor((now - lastMs) / DAY_MS) : null;
    const savedMs = recipe._savedAt ?? null;
    const dusty =
      lastCookedDays != null
        ? lastCookedDays > DUSTY_COOKED_DAYS
        : savedMs != null && now - savedMs > DUSTY_NEVER_DAYS * DAY_MS;

    let perServing = null;
    try {
      const n = recipeNutrition(recipe, { pantry, brandNutrition, getInfo });
      const cov = n?.coverage;
      if (
        n?.perServing &&
        cov?.total > 0 &&
        cov.resolved / cov.total >= 0.5 &&
        Number.isFinite(n.perServing.kcal)
      ) {
        perServing = {
          kcal: Math.round(n.perServing.kcal),
          protein_g: Number.isFinite(n.perServing.protein_g)
            ? Math.round(n.perServing.protein_g)
            : null,
        };
      }
    } catch {
      /* nutrition never blocks the digest */
    }

    rows.push({
      slug: recipe.slug,
      ownerUserId: ownerUserId || null,
      title: recipe.title,
      cuisine: recipe.cuisine || null,
      category: recipe.category || null,
      timeMinutes: totalTimeMin(recipe) || null,
      perServing,
      lastCookedDays,
      dusty,
    });
  };

  for (const r of RECIPES) push(r, null);
  for (const ur of userRecipes) {
    if (!ur?.recipe) continue;
    push(
      { ...ur.recipe, _savedAt: ur.createdAt ? new Date(ur.createdAt).getTime() : null },
      ur.userId,
    );
  }

  if (rows.length <= DIGEST_CAP) return rows;

  // Over cap: keep dusty + goal-relevant rows first. Goal relevance
  // is coarse on purpose — high-protein weeks favor protein-dense
  // rows, low-calorie weeks favor lighter ones; everything else ties.
  const goalScore = (r) => {
    if (!r.perServing) return 0;
    if (goal === "high_protein") return r.perServing.protein_g ?? 0;
    if (goal === "low_calorie") return r.perServing.kcal ? -r.perServing.kcal : 0;
    return 0;
  };
  return [...rows]
    .sort((a, b) => {
      if (a.dusty !== b.dusty) return a.dusty ? -1 : 1;
      return goalScore(b) - goalScore(a);
    })
    .slice(0, DIGEST_CAP);
}
